import PropTypes from "prop-types";
import * as React from "react";
import { ModalStep, TooltipStep, Tour as TourUI, TourProvider } from "@skbkontur/react-ui-tour";

import { isNotNullOrUndefined, reject } from "../../../../../Common/TypingUtils";
import { scrollElementInCenter } from "../../../Engine/utils";
import {
    formDidMountEventPath,
    formShowTourEventPath,
    formTourStepsShownEvent,
} from "../../../Engine/Core/EventManagement/ExternalEvents/ExternalEvents";
import { IExternalEventManager } from "../../../Engine/Core/EventManagement/ExternalEvents/ExternalEventManager";
import { CandyDispatch } from "../../../Engine/Core/Store/CandyReduxTypes";
import { pullContext } from "../../TypingUtils";
import { ITourElementsRegistry } from "../../Internals/TourManagement/ITourElementsRegistry";
import {
    ControlReference,
    getAnchorElementByControlReferenceOrUndefined,
} from "../../../Engine/Core/ControlDispatcher/ControlReference";

import { TourModalStepContent } from "./TourModalStepContent/TourModalStepContent";
import { showTourButtonTourStep as showTourButton } from "./Steps/ShowTourButtonStep";
import { beforeTourAction, events as tourEvents } from "./TourEvents";
import { sendMetric, sendOnShowMetric, sendOnShownMetric } from "./TourMetrics";
import { Actions, Data, Step, TourContext } from "./TourTypes";

export interface TourProps {
    steps: Step[];
    shownTours: undefined | string[];
    isDisabledTour: boolean;
    dispatch: CandyDispatch;
    tourElementsRegistry: ITourElementsRegistry;
    externalEventManager: IExternalEventManager;
    isFormInFormNds?: boolean;
}

interface TourState {
    steps: Step[];
    tourId: number;
}

function getLegacySteps(kewebSteps: string[]): string[] {
    const shownSteps = [];

    if (kewebSteps.includes("candyform_gfv_110302")) {
        shownSteps.push("candyform_starttour");
        shownSteps.push("candyform_pin");
        shownSteps.push("candyform_colgroupbutton");
        shownSteps.push("candyform_addrowbutton");
        shownSteps.push("candyform_totalamount");
        shownSteps.push("candyform_feedbackbutton");
    }

    if (window.localStorage.getItem("rowMenuTooltipWasShown")) {
        shownSteps.push("candyform_rowmenu");
    }

    return shownSteps;
}

export class Tour extends React.Component<TourProps, TourState> {
    public static contextTypes = {
        getTourSteps: PropTypes.func,
        track: PropTypes.func,
    };

    public static defaultProps = { steps: [] };

    public state: TourState = { steps: [], tourId: 0 };

    private readonly tour = React.createRef<TourUI>();
    private readonly stepsData: Data = getStepData();
    private readonly shownSteps: string[] = [];

    public constructor(props: TourProps, context: TourContext) {
        super(props, context);
        this.props.externalEventManager.onceExternalEvent(formDidMountEventPath(), () => this.initTour());
    }

    public componentDidMount(): void {
        this.props.externalEventManager.onExternalEvent(formShowTourEventPath(), this.handleRunTour);
    }

    public componentWillUnmount(): void {
        this.props.externalEventManager.offExternalEvent(formShowTourEventPath(), this.handleRunTour);
    }

    public render(): null | JSX.Element {
        if (this.props.isDisabledTour || !this.state.steps.length) {
            return null;
        }

        return (
            <TourProvider onTourShown={this.onTourShown}>
                <TourUI id="CandyTour" ref={this.tour}>
                    {this.renderSteps(this.state.steps)}
                </TourUI>
            </TourProvider>
        );
    }

    private initTour(): void {
        const { shownTours } = this.props;

        if (!shownTours) {
            return;
        }

        const shownSteps = [...shownTours, ...getLegacySteps(shownTours)];

        const steps = this.mergePropsAndEngineSteps().filter(step => !shownSteps.includes(step.id.toLowerCase()));

        this.showTour(steps);
    }

    private readonly runTour = (steps: Step[] = this.mergePropsAndEngineSteps()) => {
        this.showTour(steps, this.tour.current?.run ?? undefined);
    };

    private beforeTour(steps: Step[]): void {
        sendOnShowMetric(this.getContext(), steps);
        if (steps[0] != undefined) {
            this.props.dispatch(beforeTourAction(steps[0]));
        }
    }

    private getControl(step: Step): undefined | ControlReference {
        if (step.controlId) {
            return this.props.tourElementsRegistry.getLastControl(step.controlId);
        }
        return undefined;
    }

    private showTour(steps: Step[], runTour?: () => void): void {
        const toRenderSteps: Step[] = steps
            .map((step: Step): undefined | null | Step => {
                if (step.type === "modal" || step.control || step.isEnabled) {
                    return step;
                }

                const control = this.getControl(step);

                return control != undefined
                    ? { ...step, control: step.group && step.group.includes("menu") ? undefined : control }
                    : null;
            })
            .filter(isNotNullOrUndefined)
            .sort(({ order = 0 }: Step, { order: orderB = 0 }: Step) => order - orderB);

        if (toRenderSteps.length > 0) {
            this.beforeTour(toRenderSteps);
            this.setState({ steps: toRenderSteps }, runTour);
        }
    }

    private sendShownSteps(): void {
        const shownIds = this.state.steps.map(step => step.id);
        this.props.externalEventManager.triggerExternalEvent(formTourStepsShownEvent(shownIds));
    }

    private getStepEvents(index: number): () => Promise<void> {
        return async () => {
            const prevStep: undefined | Step = this.stepsData.get("currStep");
            const step = this.state.steps[index] ?? reject("Current step is not defined");

            this.stepsData.set("currStep", step);
            this.stepsData.set("prevStep", prevStep);

            const nextGroup = step.group || [];
            const prevGroup = prevStep && prevStep.group ? [...prevStep.group].reverse() : [];

            const afterEvents = prevGroup
                .filter(group => !nextGroup.includes(group))
                .map(group => tourEvents[group]?.after)
                .filter(isNotNullOrUndefined);
            const beforeEvents = nextGroup
                .filter(group => !prevGroup.includes(group))
                .map(group => tourEvents[group]?.before)
                .filter(isNotNullOrUndefined);

            const events = [...afterEvents, ...beforeEvents];
            for (const event of events) {
                await this.props.dispatch(event(this.stepsData));
            }
        };
    }

    private getTarget(step: Step): () => Element | Text | null {
        return () => {
            const control = step.control ? step.control : this.getControl(step);
            const element = getAnchorElementByControlReferenceOrUndefined(control);
            if (element != undefined) {
                scrollElementInCenter(element);
            }
            return element ?? null;
        };
    }
    private readonly onOpen = (index: number) => () => {
        const { id } = this.state.steps[index] ?? reject();

        this.shownSteps.push(id);

        sendMetric(this.getContext(), this.stepsData, index);
    };

    private readonly onTourShown = async () => {
        sendOnShownMetric(this.getContext());
        const { shownTours } = this.props;

        const stepShowTour = this.getContext()
            .getTourSteps()
            .find(({ id }: Step) => id === showTourButton.id);
        const userIsNotVisibleShowTourButtonStep =
            shownTours && !shownTours.includes(showTourButton.id) && !this.shownSteps.includes(showTourButton.id);

        if (userIsNotVisibleShowTourButtonStep && stepShowTour != undefined) {
            this.runTour([stepShowTour]);
        } else {
            const step = this.stepsData.get("currStep") as Step;
            const stepGroup = step.group ?? [];
            const groups = [...stepGroup].reverse();

            this.stepsData.set("prevStep", step);

            for (const group of groups) {
                const tourEvent = tourEvents[group];
                if (tourEvent != undefined) {
                    await this.props.dispatch(tourEvent.after(this.stepsData));
                }
            }
            await this.stepsData.reset();
        }

        this.sendShownSteps();
    };

    private renderSteps(steps: Step[]): JSX.Element[] {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        return steps.map((step: Step, index: number) =>
            step.type === "modal" ? (
                <ModalStep
                    key={step.id}
                    render={getModal(step, index === 0, steps.length, index)}
                    onBefore={this.getStepEvents(index)}
                    onOpen={this.onOpen(index)}
                />
            ) : (
                <TooltipStep
                    content={<div>{step.content}</div>}
                    header={step.header}
                    key={step.id}
                    offset={step.offset}
                    allowedPositions={step.positions}
                    anchorElement={this.getTarget(step) as any}
                    onBefore={this.getStepEvents(index)}
                    onOpen={this.onOpen(index)}
                />
            )
        );
        /* eslint-enable @typescript-eslint/no-explicit-any */
    }

    private readonly handleRunTour = () => {
        this.runTour();
    };

    private getContext(): TourContext {
        return pullContext<TourContext>(this);
    }

    private mergePropsAndEngineSteps(): Step[] {
        if (this.props.steps.length) {
            const filteredGeneratorSteps = this.props.steps.filter(
                step => !step.group?.includes("isFormInFormNds") || this.props.isFormInFormNds
            );
            const filteredEngineSteps = this.getContext()
                .getTourSteps()
                .filter(
                    step => !this.props.steps.some(propStep => propStep.id.toLowerCase() === step.id.toLowerCase())
                );

            return [...filteredGeneratorSteps, ...filteredEngineSteps];
        }
        return this.getContext().getTourSteps();
    }
}

function getModal(
    step: Step,
    isFirst: boolean,
    points: number,
    activePoint: number
): (actions: Actions) => JSX.Element {
    // eslint-disable-next-line react/display-name
    return (actions: Actions) => (
        <TourModalStepContent
            {...actions}
            {...step}
            activePoint={activePoint}
            isFirst={isFirst}
            points={points}
            activeStepIndex={activePoint}
        />
    );
}

function getStepData(): Data {
    // NOTE Дрянь написана, можно напихать, что угодно. У кого руки дойдут -- исправте.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let data: { [key: string]: any } = {};

    return {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        set: (field: string, value: any): void => {
            data[field] = value;
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        get: (field: string): any => data[field],
        remove: (field: string): void => {
            delete data[field];
        },
        reset: () => {
            data = {};
        },
    };
}
